import {
  DELETE_SUCCESS_MESSAGE,
  DEFAULT_PAGE_INDEX_INPUT,
  UPDATE_SUCCESS_MESSAGE,
  DEFAULT_PAGE_SIZE,
} from "constants/index";

import React from "react";

import {
  Box,
  Grid,
  Breadcrumbs as MUIBreadcrumbs,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Button, PlusIcon } from "components";
import Eye from "components/Icon/Eye";
import EyeOff from "components/Icon/EyeOff";
import Trash01 from "components/Icon/Trash01";
import { confirm } from "contexts";
import dayjs from "dayjs";
import {
  NotificationEntity,
  useDeleteOneBaseNotificationControllerNotificationEntity,
  useGetManyBaseNotificationControllerNotificationEntity,
  useGetManyBaseNotificationControllerNotificationEntityInfinite,
  useNotificationControllerDeleteAllMyNotifications,
  useNotificationControllerUpdateMyNotifications,
} from "generated";
import { useNotification } from "hooks/useNotification";
import { useSearchParams, useUpdateSearch } from "hooks/useSearchParams";
import { useLocation, useNavigate } from "react-router-dom";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";

import { NotificationItem } from "./NotificationItem";
import { NotificationNoFound } from "./NotificationNoFound";

export const NotificationList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { search, pathname } = useLocation();
  const { handleSearchClick } = useUpdateSearch(pathname, search);
  const [query, setQuery] = useQueryParams({
    pageIndex: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  });
  const { pageIndex, pageSize } = query;
  const { data, isLoading, refetch } =
    useGetManyBaseNotificationControllerNotificationEntity({
      s: JSON.stringify({}),
      limit: pageSize,
      offset: (pageIndex - 1) * pageSize,
    });

  const {
    mutate: deleteOne,
    error: errDeleteOne,
    isSuccess: isSuccessDeleteOne,
  } = useDeleteOneBaseNotificationControllerNotificationEntity({
    mutation: {
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries({
          queryKey: ["/notification/count-my-notifications"],
        });
      },
    },
  });

  const {
    mutate: deleteAll,
    error: errDeleteAll,
    isSuccess: isSuccessDeleteAll,
  } = useNotificationControllerDeleteAllMyNotifications({
    mutation: {
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries({
          queryKey: ["/notification/count-my-notifications"],
        });
      },
    },
  });

  const { mutate, error, isSuccess } =
    useNotificationControllerUpdateMyNotifications({
      mutation: {
        onSuccess: () => {
          refetch();
          queryClient.invalidateQueries({
            queryKey: ["/notification/count-my-notifications"],
          });
        },
      },
    });

  const handleDeleteOneNotification = (id: string) => {
    deleteOne({ id });
  };

  const handleDeleteAllNotification = () => {
    confirm({
      title: "Confirm deletion",
      content: "You are deleting all notifications. Are you sure?",
    })
      .then(() => {
        deleteAll();
      })
      .catch(() => {});
  };

  const handleUnReadAllNotification = () => {
    mutate({ params: { isRead: false } });
  };

  const handleReadAllNotification = () => {
    mutate({ params: { isRead: true } });
  };

  const handleReadOneNotification = (id: string) => {
    mutate({ params: { id, isRead: true } });
  };

  useNotification(errDeleteOne, isSuccessDeleteOne, DELETE_SUCCESS_MESSAGE);
  useNotification(errDeleteAll, isSuccessDeleteAll, DELETE_SUCCESS_MESSAGE);
  useNotification(error, isSuccess, UPDATE_SUCCESS_MESSAGE);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          height: "40px",
          marginBottom: "28px",
        }}
      >
        <MUIBreadcrumbs aria-label="breadcrumb">
          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: "600",
            }}
            color="text.primary"
          >
            Notification
          </Typography>
        </MUIBreadcrumbs>
        {data?.data?.data && data.data.data.length > 0 && (
          <Box sx={{ alignSelf: "center" }}>
            <Tooltip title="Delete All">
              <Button
                variant="outlined"
                sx={{
                  marginRight: "8px",
                }}
                onClick={() => {
                  handleDeleteAllNotification();
                }}
                startIcon={<Trash01 />}
              >
                Delete All
              </Button>
            </Tooltip>
            <Tooltip arrow title="Mark as un-read all">
              <Button
                variant="outlined"
                sx={{
                  marginRight: "8px",
                }}
                onClick={() => {
                  handleUnReadAllNotification();
                }}
                startIcon={<EyeOff />}
              >
                Mark as un-read all
              </Button>
            </Tooltip>
            <Tooltip arrow title="Mark as read all">
              <Button
                onClick={() => {
                  handleReadAllNotification();
                }}
                startIcon={<Eye />}
              >
                Mark as read all
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
      {data?.data?.data && data.data.data.length > 0 && (
        <>
          {data.data.data.map((value: NotificationEntity) => (
            <Grid key={value.id} marginBottom="14px">
              <NotificationItem
                data={value}
                handleDeleteOneNotification={handleDeleteOneNotification}
                handleReadOneNotification={handleReadOneNotification}
              />
            </Grid>
          ))}
          <Pagination
            count={data?.data.pageCount}
            sx={{ display: "flex", justifyContent: "end" }}
            color="primary"
            page={pageIndex}
            onChange={(_, e) => handleSearchClick({ pageIndex: e })}
          />
        </>
      )}
      {data?.data?.data && data.data.data.length === 0 && (
        <Grid position="sticky" top="40%">
          <NotificationNoFound />
        </Grid>
      )}
    </>
  );
};
